import StyledTable, { Align } from '../../../StyledTable/StyledTable';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import classNames from 'classnames/bind';
import styles from './OrderHistoryPanel.module.scss';
import { useEffect, useState } from 'react';
import { Stack } from '@mui/system';
import { getOrderHistoryPublic } from '@/controllers/gridManager/masterTransManager/masterTransManager';
import dayjs from 'dayjs';
import ViewNumberFormat from '@/views/elements/Inputs/ViewNumberFormat';
import { useCosInfo } from '@/controllers/gridManager/accountsManager/accountManager';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utilities';

const cx = classNames.bind(styles);

type OrderHistoryPanelProps = {
  accountId?: string;
  mtAccountId?: string;
}

const OrderHistoryPanel = ({ accountId, mtAccountId }: OrderHistoryPanelProps) => {
  const { t } = useTranslation()
  const [tableData, setTableData] = useState<any>([]);
  const [pagination, setPagination] = useState<{ rowsPerPage: number, total: number }>({
    rowsPerPage: 5,
    total: 0
  });
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>();
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>();
  const { cosInfo } = useCosInfo()

  useEffect(() => {
    onLoadData(1, pagination.rowsPerPage)
  }, [fromDate, toDate])

  const tradeTypeRenderer = (value: string) => {
    return <div style={{ color: value === 'B' ? '#45B879' : '#E84553' }}>{value === 'B' ? 'Mua' : 'Bán'}</div>
  }

  const cookedTradeHistory = (raw: any[]) => {
    return raw.map(r => ({
      tradeDay: r.on_date,
      symbol: r.symbol,
      tran_type: r.tran_type,
      exec_qtty: r.exec_qtty,
      trade_price: r.trade_price,
      exec_amt: r.exec_amt,
    }))
  }

  const onLoadData = async (page: number, limit: number) => {
    let response = await getOrderHistoryPublic(
      accountId || cosInfo.refaccount,
      mtAccountId || '',
      fromDate ? formatDate(fromDate) : '',
      toDate ? formatDate(toDate) : '',
      page,
      limit
    )
    if (response.isSuccess) {
      setTableData(cookedTradeHistory(response.data));
      setPagination({
        rowsPerPage: limit,
        total: response.data[0]?.row_count || 0
      });
    }
  };

  const tableConfig = {
    fields: [
      { valueName: "tradeDay", name: t('copytrade.OrderHistory.tradeDay'), width: "10%", align: Align.left },
      { valueName: "symbol", name: t('copytrade.common.symbol'), width: "10%", align: Align.left },
      { valueName: "tran_type", name: t('copytrade.OrderHistory.tran_type'), width: "20%", align: Align.left, render: tradeTypeRenderer },
      { valueName: "exec_qtty", name: t('copytrade.OrderHistory.exec_qtty'), width: "20%", align: Align.right, render: (value: any) => <ViewNumberFormat displayType='text' value={value} /> },
      { valueName: "trade_price", name: t('copytrade.OrderHistory.trade_price'), width: "20%", align: Align.right, render: (value: any) => <ViewNumberFormat displayType='text' value={value} /> },
      { valueName: "exec_amt", name: t('copytrade.OrderHistory.exec_amt'), width: "20%", align: Align.right, render: (value: any) => <ViewNumberFormat displayType='text' value={value} /> },
    ]
  };

  return (
    <div className={cx('order-history')}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={['DatePicker']}>
          <Stack direction='row' gap={2} sx={{ marginBottom: '15px !important' }}>
            <DatePicker value={fromDate} format='DD/MM/YYYY' onChange={(value) => setFromDate(value)} sx={{ width: '160px' }} label={t('copytrade.common.fromDate')} />
            <DatePicker value={toDate} format='DD/MM/YYYY' onChange={(value) => setToDate(value)} sx={{ width: '160px' }} label={t('copytrade.common.toDate')} />
          </Stack>
        </DemoContainer>
      </LocalizationProvider>
      <StyledTable
        tableConfig={tableConfig}
        tableData={tableData}
        pagination={pagination}
        onPageChange={onLoadData}
        onLimitChange={onLoadData}
        onRowClick={() => { }}
      ></StyledTable>
    </div>
  );
}

export default OrderHistoryPanel;